import { useCallback, useEffect, useState } from 'react';

import { useDidUpdate } from 'Shared/hooks';

import { Plan } from '../Rate/PaymentPlan/types';

export type State = 'IL' | 'TX' | 'default';

export type ConfigParams = {
  [key: string]: any;
};

export type DynamicConfig = {
  title: (params: ConfigParams) => string;
  subtitle: (params: ConfigParams) => string;
};

export type DynamicConfigState = {
  monthly: DynamicConfig | ((isMonthlyPlanOnly: boolean) => DynamicConfig);
  full: DynamicConfig | ((isPayInFullOnly: boolean) => DynamicConfig);
};

export type PaymentConfig = {
  [state in State]: DynamicConfigState;
} & {
  [key: string]: DynamicConfigState;
};

const config: PaymentConfig = {
  IL: {
    monthly(isMonthlyPlanOnly) {
      return {
        title: isMonthlyPlanOnly ? () => 'Total Cost:' : () => 'Due today:',
        subtitle: isMonthlyPlanOnly
          ? () => ''
          : (params) =>
              `+${params.totalPayments} payments of ${params.installmentAmount}`,
      };
    },
    full(isPayInFullOnly) {
      return {
        title: isPayInFullOnly ? () => 'Due today:' : () => 'Total Cost:',
        subtitle: isPayInFullOnly
          ? (params) => `Total Cost: ${params.detailsAmount}`
          : (params) => `Save with pay in full ${params.detailsAmount}`,
      };
    },
  },
  TX: {
    monthly(isMonthlyPlanOnly) {
      return {
        title: isMonthlyPlanOnly ? () => 'Total Cost:' : () => 'Due today:',
        subtitle: isMonthlyPlanOnly
          ? () => ''
          : (params) =>
              `+${params.totalPayments} payments of ${params.installmentAmount}`,
      };
    },
    full(isPayInFullOnly) {
      return {
        title: isPayInFullOnly ? () => 'Due today:' : () => 'Total Cost:',
        subtitle: isPayInFullOnly
          ? (params) => `Total Cost: ${params.detailsAmount}`
          : (params) => `Save with pay in full ${params.detailsAmount}`,
      };
    },
  },
  default: {
    monthly(isMonthlyPlanOnly) {
      return {
        title: isMonthlyPlanOnly ? () => 'Total Cost:' : () => 'Pay monthly:',
        subtitle: (params) => `6-month premium: ${params.totalPremiumAmount}`,
      };
    },
    full: {
      title: () => 'Pay in full:',
      subtitle: (params) => `Save up to ${params.discountPercentage}`,
    },
  },
};

const isFunction = (value: any): value is Function => {
  return typeof value === 'function';
};

const useFooterConfig = (
  state: string,
  plan: Plan,
  params: ConfigParams,
  isFullPlanOnly: boolean,
  isMonthlyPlanOnly: boolean
) => {
  const [footerConfig, setFooterConfig] = useState<{
    title: string;
    subtitle: string;
  }>();

  const hasStateConfigDefined = (state: string) => {
    return !!config[state];
  };

  const getPaymentConfig = useCallback(
    (params: ConfigParams) => {
      const stateConfig = hasStateConfigDefined(state)
        ? config[state]
        : config.default;

      if (plan === 'full') {
        if (isFunction(stateConfig.full)) {
          const fullConfig = stateConfig.full(isFullPlanOnly);

          return {
            title: fullConfig.title(params),
            subtitle: fullConfig.subtitle(params),
          };
        }

        return {
          title: stateConfig.full.title(params),
          subtitle: stateConfig.full.subtitle(params),
        };
      } else {
        if (isFunction(stateConfig.monthly)) {
          const monthlyConfig = stateConfig.monthly(isMonthlyPlanOnly);

          return {
            title: monthlyConfig.title(params),
            subtitle: monthlyConfig.subtitle(params),
          };
        }

        return {
          title: stateConfig.monthly.title(params),
          subtitle: stateConfig.monthly.subtitle(params),
        };
      }
    },
    [state, plan, isFullPlanOnly, isMonthlyPlanOnly]
  );

  useEffect(() => {
    setFooterConfig(getPaymentConfig(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPaymentConfig]);

  useDidUpdate(() => {
    setFooterConfig(getPaymentConfig(params));
  }, [getPaymentConfig, params]);

  return { ...footerConfig };
};

export default useFooterConfig;
